<template>
  <section class="rlist-card">
    <div class="rlist-header">
      <div class="rlist-header-main">
        <span class="rlist-icon-pill">
          <svg v-if="isInProgress" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <path d="M12 7v5l3 2"/>
          </svg>
          <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <path d="M16 2v4M8 2v4M3 10h18"/>
          </svg>
        </span>
        <div>
          <h2 class="rlist-title">{{ subtitle }}</h2>
          <p class="rlist-subtitle">{{ sectionDescription }}</p>
        </div>
      </div>
      <span class="rlist-count-badge">{{ races.length }} races</span>
    </div>

    <div v-if="loading" class="rlist-grid">
      <div v-for="n in 3" :key="'race-list-skeleton-' + n" class="rlist-skeleton">
        <div class="rlist-sk-media sk-shimmer"></div>
        <div class="rlist-sk-top">
          <div class="rlist-sk-pill sk-shimmer"></div>
          <div class="rlist-sk-date sk-shimmer"></div>
        </div>
        <div class="rlist-sk-title sk-shimmer"></div>
        <div class="rlist-sk-line sk-shimmer"></div>
        <div class="rlist-sk-actions">
          <div class="rlist-sk-button sk-shimmer"></div>
          <div class="rlist-sk-icon sk-shimmer"></div>
        </div>
      </div>
    </div>

    <div v-else-if="races.length" class="rlist-grid">
      <race-item
        :type="type"
        :info="race"
        v-for="race in races"
        :key="race.id"
        @delete="$emit('delete', $event)">
      </race-item>
    </div>

    <div v-else class="rlist-empty">
      <svg class="rlist-empty-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M8 6h13M8 12h13M8 18h13"/>
        <path d="M3 6h.01M3 12h.01M3 18h.01"/>
      </svg>
      <p class="rlist-empty-title">{{ messageEmptyList || 'No races found.' }}</p>
    </div>
  </section>
</template>

<script>
import RaceItem from '@/components/Race/RaceItem'

export default {
  name: 'race-list',
  props: {
    subtitle: {
      type: String,
      required: true
    },
    races: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    messageEmptyList: {
      type: String,
      required: false
    },
    description: {
      type: String,
      required: false
    },
    type: {
      type: String,
      required: false
    }
  },
  computed: {
    isInProgress () {
      return String(this.subtitle).toLowerCase().indexOf('progress') !== -1
    },
    sectionDescription () {
      if (this.description) {
        return this.description
      }
      return this.isInProgress
        ? 'Live sessions that have started and are awaiting completion'
        : 'Scheduled sessions ready for trainer launch'
    }
  },
  components: {
    RaceItem
  }
}
</script>
<style scoped>
.rlist-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.rlist-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.rlist-header-main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rlist-icon-pill {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.rlist-title {
  margin: 0;
  color: #1a1a2e;
  font-size: 16px;
  line-height: 1.2;
  font-weight: 700;
}

.rlist-subtitle {
  margin: 4px 0 0;
  color: #8a8a9a;
  font-size: 12px;
  line-height: 1.35;
}

.rlist-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: #fafafa;
  border: 1px solid #e8e8ed;
  color: #5b4fcf;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.rlist-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 360px));
  justify-content: start;
  align-items: start;
  gap: 1rem;
}

.rlist-empty {
  border: 1px dashed #d8d8e4;
  border-radius: 12px;
  min-height: 136px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #fafafa;
  color: #8a8a9a;
  text-align: center;
  padding: 1.5rem;
}

.rlist-empty-icon {
  color: #c8c8d8;
}

.rlist-empty-title {
  margin: 0;
  color: #8a8a9a;
  font-size: 13px;
  font-weight: 600;
}

.rlist-skeleton {
  border: 1px solid #e8e8ed;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.rlist-sk-media {
  width: 100%;
  aspect-ratio: 16 / 9;
  min-height: 146px;
  border-radius: 0;
}

.rlist-sk-top,
.rlist-sk-title,
.rlist-sk-line,
.rlist-sk-actions {
  margin-left: 1rem;
  margin-right: 1rem;
}

.rlist-sk-top,
.rlist-sk-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.rlist-sk-top {
  margin-top: 1rem;
}

.rlist-sk-pill {
  width: 78px;
  height: 24px;
  border-radius: 999px;
}

.rlist-sk-date {
  width: 96px;
  height: 14px;
}

.rlist-sk-title {
  width: 72%;
  height: 20px;
  margin-top: 14px;
}

.rlist-sk-line {
  width: 88%;
  height: 12px;
  margin-top: 14px;
}

.rlist-sk-actions {
  margin-top: 14px;
  margin-bottom: 1rem;
}

.rlist-sk-button {
  width: 92px;
  height: 33px;
  border-radius: 8px;
}

.rlist-sk-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

@keyframes sk-shimmer {
  0% {
    background-position: -600px 0;
  }

  100% {
    background-position: 600px 0;
  }
}

@media (max-width: 700px) {
  .rlist-card {
    padding: 1.25rem;
  }

  .rlist-header {
    flex-direction: column;
  }

  .rlist-grid {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// RaceList.vue?278407bf